import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

import { cn } from "@/libs/cn";
import { Skeleton } from "@/modules/common/components";
import { RegistrationsEvolutionResponse } from "@/service/response/admin-dashboard";

import { DASHBOARD_CHART_SERIES } from "./constants";
import { computeChartYAxisDomain, formatChartNumberTick, formatMonthAxisTick, getChartYAxisWidth } from "./utils";

interface DashboardChartParams {
  chartData?: RegistrationsEvolutionResponse;
  isLoading?: boolean;
  className?: string;
}

const tooltipContentStyle = {
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  fontSize: 12,
};

const formatTooltipValue = (value: ValueType | undefined, name: NameType | undefined) => [formatChartNumberTick(Number(value ?? 0)), name];

type LegendSeriesItem = { value: string; dataKey: string; color: string; inactive: boolean };

const ChartLegend = ({ items, onToggleSeries }: { items: LegendSeriesItem[]; onToggleSeries: (dataKey: string) => void }) => (
  <ul className="flex flex-wrap items-center gap-4">
    {items.map((item) => (
      <li key={item.dataKey}>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm transition-opacity",
            item.inactive ? "opacity-40" : "opacity-100 hover:opacity-90",
          )}
          onClick={() => onToggleSeries(item.dataKey)}
          aria-pressed={!item.inactive}
        >
          <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden />
          <span className="text-ui-text">{item.value}</span>
        </button>
      </li>
    ))}
  </ul>
);

const DashboardChart = ({ chartData, isLoading, className }: DashboardChartParams) => {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  const data = chartData ?? [];

  const visibleSeries = useMemo(() => DASHBOARD_CHART_SERIES.filter((s) => !hiddenKeys.has(s.dataKey)), [hiddenKeys]);

  const legendPayload: LegendSeriesItem[] = DASHBOARD_CHART_SERIES.map((s) => ({
    value: s.label,
    dataKey: s.dataKey,
    color: s.color,
    inactive: hiddenKeys.has(s.dataKey),
  }));

  const { min, max, ticks } = useMemo(() => {
    const values = data.flatMap((row) => visibleSeries.map((s) => row[s.dataKey]));
    return computeChartYAxisDomain(values);
  }, [data, visibleSeries]);

  const yAxisWidth = useMemo(() => getChartYAxisWidth(ticks), [ticks]);

  const toggleSeries = (dataKey: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
        return next;
      }
      const visibleCount = DASHBOARD_CHART_SERIES.filter((s) => !next.has(s.dataKey)).length;
      if (visibleCount <= 1) return prev;
      next.add(dataKey);
      return next;
    });
  };

  const hasData = data.some((row) => row.managementCompanies !== 0 || row.clients !== 0);

  return (
    <div className={cn("rounded-xl border border-[#E2E8F0] overflow-x-auto bg-white p-4 sm:p-5", className)}>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start">
            <p className="text-[16px] font-bold text-ui-primary">Evolução de cadastros</p>
            <p className="text-[14px] text-ui-text-muted">Últimos 12 meses - Gestoras e clientes</p>
          </div>
        </div>

        <ChartLegend items={legendPayload} onToggleSeries={toggleSeries} />
      </div>

      <div className="relative flex h-80 w-full min-w-160 flex-col">
        {isLoading ? (
          <Skeleton className="h-full w-full rounded-lg" />
        ) : !hasData ? (
          <div className="flex h-full w-full items-center justify-center rounded-lg">
            <p className="text-sm text-ui-text-muted">Nenhum cadastro no período.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 8 }}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonthAxisTick}
                tick={{ fill: "#748395", fontSize: 11 }}
                axisLine={{ stroke: "#E2E8F0" }}
                tickLine={false}
              />
              <YAxis
                domain={[min, max]}
                ticks={ticks}
                tickFormatter={formatChartNumberTick}
                tick={{ fill: "#748395", fontSize: 11 }}
                axisLine={{ stroke: "#E2E8F0" }}
                tickLine={false}
                width={yAxisWidth}
              />
              <Tooltip
                cursor={{ stroke: "#CBD5E1", strokeWidth: 1 }}
                contentStyle={tooltipContentStyle}
                formatter={formatTooltipValue}
                labelFormatter={(label) => formatMonthAxisTick(String(label))}
              />
              {visibleSeries.map((s) => (
                <Area
                  key={s.dataKey}
                  type="monotone"
                  dataKey={s.dataKey}
                  name={s.label}
                  stroke={s.color}
                  fill={s.color}
                  fillOpacity={0.1}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2, stroke: "#fff", fill: s.color }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DashboardChart;
